<template>
  <div>
    <LabelledInput label="">
      <b-button
        variant="primary"
        block
        data-test="add-content-panel"
        @click="addContentPanel"
      >
        Add Content Panel
      </b-button>
    </LabelledInput>

    <ReorderableList
      v-if="contentPanels.length > 0"
      :items.sync="contentPanels"
      :selected.sync="selectedContentPanelKey"
    />

    <hr class="mt-4">

    <InfoCardContentPanelSettings
      v-if="selectedContentPanel"
      data-test="content-panel-settings"
      :panel="selectedContentPanel"
      @propertyChanged="onPanelPropertyChanged"
      @delete="onPanelDelete"
    />
  </div>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'
import { v4 as uuidv4 } from 'uuid'

import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import ReorderableList from '@/components/common/ReorderableList.vue'
import InfoCardContentPanelSettings from './InfoCardContentPanelSettings.vue'
import { CustomTabConfigKeys, CustomTabNode, InfoCardContentPanel } from '@/types'
import { NODE_CONFIG_KEY } from '@/constants'
import { calculateNextIdFromNames } from '@/store/helpers'

const components = {
  LabelledInput,
  ReorderableList,
  InfoCardContentPanelSettings,
}

const props = {
  tabNode: Object as PropType<CustomTabNode>,
}

@Component({ components })
export default class InfoCardContentPanels extends Vue.extend({ props }) {
  selectedPanelKey: string | null = null

  get selectedContentPanelKey () {
    return this.selectedPanelKey || this.contentPanels[0].key
  }

  set selectedContentPanelKey (key: string) {
    this.selectedPanelKey = key
  }

  get selectedContentPanel () {
    return this.contentPanels.find(x => x.key === this.selectedContentPanelKey)
  }

  get selectedContentPanelIndex () {
    return this.contentPanels.findIndex(x => x.key === this.selectedContentPanelKey)
  }

  get contentPanels () {
    return this.tabNode.config.infoCardContentPanels ?? []
  }

  set contentPanels (newContentPanels) {
    this.updateInfoCardContentPanels(newContentPanels)
  }

  addContentPanel () {
    const nextId = calculateNextIdFromNames(this.contentPanels.map(x => x.label))
    const newPanel = {
      key: `${uuidv4().replace(/-/g, '')}`,
      label: `Panel ${nextId}`,
    }
    this.contentPanels = [...this.contentPanels, newPanel]
  }

  onPanelPropertyChanged (propertyName: string, newValue: any) {
    this.updateInfoCardContentPanels(newValue, String(this.selectedContentPanelIndex), propertyName)
  }

  onPanelDelete () {
    const newContentPanels = this.contentPanels.filter(panel => panel.key !== this.selectedContentPanelKey)
    this.contentPanels = newContentPanels
  }

  updateInfoCardContentPanels (value: InfoCardContentPanel[] | string, ...args: string[]) {
    this.$typedStore.primary.entities.nodes.updateNodeProp({
      path: this.tabNode.path,
      configPath: [NODE_CONFIG_KEY, CustomTabConfigKeys.INFO_CARD_CONTENT_PANELS, ...args],
      value,
    })
  }
}

</script>
